import { Switch } from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';

type SwitcherProps = {
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'success'
    | 'info'
    | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  disabled?: boolean;
};

const sizeClasses = {
  small: {
    track: 'h-4 w-8',
    thumb: 'h-3 w-3',
    thumbChecked: 'translate-x-4',
  },
  medium: {
    track: 'h-6 w-11',
    thumb: 'h-5 w-5',
    thumbChecked: 'translate-x-5',
  },
  large: {
    track: 'h-8 w-14',
    thumb: 'h-7 w-7',
    thumbChecked: 'translate-x-7',
  },
};

const colorClasses = {
  primary: {
    trackEnabled: 'bg-primary',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
  secondary: {
    trackEnabled: 'bg-neutral-400',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
  error: {
    trackEnabled: 'bg-error',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
  warning: {
    trackEnabled: 'bg-yellow-500',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
  success: {
    trackEnabled: 'bg-success',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
  info: {
    trackEnabled: 'bg-blue-500',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
  default: {
    trackEnabled: 'bg-neutral-400',
    trackDisabled: 'bg-neutral-100',
    thumb: 'bg-neutral-0',
  },
};

export const Switcher = ({
  enabled,
  setEnabled,
  color = 'primary',
  size = 'medium',
  className,
  disabled = false,
}: SwitcherProps) => {
  const {
    track: trackSizeClass,
    thumb: thumbSizeClass,
    thumbChecked,
  } = sizeClasses[size];
  const { trackEnabled, trackDisabled, thumb } = colorClasses[color];

  const trackClass = clsx(
    'relative inline-flex items-center rounded-full transition-colors focus:outline-none',
    trackSizeClass,
    enabled ? trackEnabled : trackDisabled,
    disabled && 'opacity-50 cursor-not-allowed',
    className,
  );

  const thumbClass = clsx(
    'inline-block rounded-full transform transition-transform ml-[2px]',
    thumbSizeClass,
    thumb,
    enabled ? thumbChecked : 'translate-x-0',
  );

  return (
    <Switch
      checked={enabled}
      onChange={setEnabled}
      className={trackClass}
      disabled={disabled}
    >
      <span className={thumbClass} />
    </Switch>
  );
};
